import { PersonRole } from "@ha/data/basic";
import { Selector } from "@ngxs/store";

import { SubscriptionsSelectors } from "../../subscriptions/states/subscriptions.selectors";
import { PermitPerson } from "../models/permit-person.model";
import { PermitRequestCandidate } from "../models/permit-request-candidate.model";
import { PermitRequest } from "../models/permit-request.model";
import { PermitToSign } from "../models/permit-to-sign.model";
import { PermitUserState, PermitUserStateModel } from "./permit-user.state";

export class PermitUserSelectors {
    @Selector([PermitUserState])
    public static currentPerson(state: PermitUserStateModel): PermitPerson | undefined {
        return state?.currentPerson;
    }

    @Selector([PermitUserState])
    public static isPremiumIssuer(state: PermitUserStateModel): boolean | undefined {
        return state?.currentPerson?.isPremiumIssuer;
    }

    @Selector([PermitUserState])
    public static hasValidCertificate(state: PermitUserStateModel): boolean | undefined {
        return state?.currentPerson?.hasValidCertificate === true;
    }

    @Selector([PermitUserState])
    public static isHotWorker(state: PermitUserStateModel): boolean | undefined {
        if (state?.isHotWorker !== undefined) {
            return state.isHotWorker;
        }

        return undefined;
    }

    @Selector([PermitUserState])
    public static canCreateBaseOrDemoPermits(state: PermitUserStateModel): boolean {
        if (state?.isHotWorker === true || state?.canCreateDemoPermits) {
            return true;
        }

        return false;
    }

    @Selector([PermitUserState])
    public static showCreateDemoButton(state: PermitUserStateModel): boolean {
        return state?.canCreateDemoPermits;
    }

    @Selector([PermitUserState])
    public static haveAccessToPermit(state: PermitUserStateModel): boolean | undefined {
        if (
            state?.isHotWorker !== undefined
            && state?.isExternal !== undefined
            && state?.canCreateDemoPermits !== undefined
            && state?.isAdministrator !== undefined
        ) {
            return state.isHotWorker || state.isExternal || state.canCreateDemoPermits || state.isAdministrator;
        }

        return undefined;
    }

    @Selector([PermitUserState, SubscriptionsSelectors.hasActiveSubscription])
    public static isHotworkerOrPremiumIssuer(
        state: PermitUserStateModel,
        hasActiveSubscription: boolean,
    ): boolean | undefined {
        if (state?.isHotWorker === true) {
            return true;
        }

        if (state?.currentPerson?.isPremiumIssuer === true) {
            return true;
        }

        return state?.isExternal === true && hasActiveSubscription && !state?.currentPerson?.isPremiumSuperintendent;
    }

    @Selector([PermitUserState])
    public static isPremiumSuperintendent(state: PermitUserStateModel): boolean | undefined {
        return state?.currentPerson?.isPremiumSuperintendent;
    }

    @Selector([PermitUserState])
    public static permitsToSign(state: PermitUserStateModel): PermitToSign[] | undefined {
        return state?.permitsToSign?.filter((_) => _.role !== PersonRole.PendingIssuer);
    }

    @Selector([PermitUserState])
    public static permitsToClaim(state: PermitUserStateModel): PermitToSign[] | undefined {
        return state?.permitsToSign?.filter((_) => _.role === PersonRole.PendingIssuer);
    }

    @Selector([PermitUserState])
    public static allPermitNotifications(state: PermitUserStateModel): PermitToSign[] | undefined {
        return state?.permitsToSign;
    }

    @Selector([PermitUserState])
    public static permitRequests(state: PermitUserStateModel): PermitRequest[] | undefined {
        return state?.permitRequests;
    }

    @Selector([PermitUserState])
    public static loading(state: PermitUserStateModel): boolean | undefined {
        return state?.loading;
    }

    @Selector([PermitUserState])
    public static permitRequestCandidates(state: PermitUserStateModel): PermitRequestCandidate[] | undefined {
        return state?.permitRequestCandidates;
    }

    @Selector([PermitUserState])
    public static loadingPermitRequestCandidates(state: PermitUserStateModel): boolean | undefined {
        return state?.loadingPermitRequestCandidates;
    }

    @Selector([PermitUserState])
    public static selectedPermitRequestCandidate(state: PermitUserStateModel): PermitRequestCandidate | undefined {
        return state?.selectedPermitRequestCandidate;
    }

    @Selector([PermitUserState])
    public static requestingPermit(state: PermitUserStateModel): boolean | undefined {
        return state?.requestingPermit;
    }

    @Selector([PermitUserState])
    public static permitRequestSubmitted(state: PermitUserStateModel): boolean | undefined {
        return state?.permitRequestSubmitted;
    }

    @Selector([PermitUserState])
    public static permitRequestErrorMessage(state: PermitUserStateModel): string | undefined {
        return state?.permitRequestErrorMessage;
    }
}
